// AnimationController.ts
import type { CombatResult, HexPoint } from "../../core/types/battle.types";
import type { UnitStack } from "../../view/renderers/UnitStack";
import { BattleBus } from '../../events/Events';



type AnimationTask = () => Promise<void>;

type QueueItem = {
  task: AnimationTask;     // Сама функція анімації
  resolve: () => void;
  reject: (err?: any) => void;
  signal?: AbortSignal;
  type: 'combat' | 'move' ; // (Опціонально) для логування або скасування конкретних типів
};

export class AnimationController {
  private queue: QueueItem[] = [];
  private running = false;

  // =========================================================
  // PUBLIC API
  // =========================================================

  /**
   * Анімація Бою (обгортка)
   */
  public playSkillAnimation(data: CombatResult, attacker: UnitStack, target: UnitStack, opts?: { signal?: AbortSignal, isOpportunityStrike?: boolean }): Promise<void> {
    return this.enqueue(async () => {
       await this.executeCombatSequence(data, attacker, target, opts?.signal, opts?.isOpportunityStrike);
    }, opts?.signal, 'combat');
  }

  /**
   * Анімація Руху (обгортка)
   */
  public playMovement(unit: UnitStack, splinePoints: any[], opts?: { signal?: AbortSignal }): Promise<void> {
    return this.enqueue(async () => {
       await this.animateUnitMovement(unit, splinePoints, opts?.signal);
    }, opts?.signal, 'move');
  }

  private enqueue(task: AnimationTask, signal?: AbortSignal, type: 'combat' | 'move' = 'combat'): Promise<void> {
    return new Promise((resolve, reject) => {
      this.queue.push({ task, resolve, reject, signal, type });
      if (!this.running) this.processQueue();
    });
  }

  

  // Enqueue and await the whole animation sequence for the event
  // public playSkillAnimation(data: CombatResult, attacker: UnitStack, target: UnitStack, opts?: { signal?: AbortSignal }): Promise<void> {
  //   return new Promise((resolve, reject) => {
  //     this.queue.push({ data, attacker, target, resolve, reject, signal: opts?.signal });
  //     if (!this.running) this.processQueue();
  //   });
  // }

  // // Core queue processor (serial)
  // private async processQueue() {
  //   if (this.running) return;
  //   this.running = true;
  //   while (this.queue.length > 0) {
  //     const item = this.queue.shift()!;
  //     try {
  //       if (item.signal?.aborted) throw new Error("animation aborted");
  //       await this.runOne(item.data, item.attacker, item.target, item.signal);
  //       item.resolve();
  //     } catch (err) {
  //       item.reject(err);
  //     }
  //   }
  //   this.running = false;
  // }

  private async processQueue() {
    if (this.running) return;
    this.running = true;

    while (this.queue.length > 0) {
      const item = this.queue.shift()!;
      
      // Перевірка на скасування до початку
      if (item.signal?.aborted) {
         item.reject(new Error("animation aborted via signal"));
         continue;
      }

      try {
        // ! МАГІЯ ТУТ: Ми просто викликаємо task(), не знаючи, що там всередині
        await item.task(); 
        item.resolve();
      } catch (err) {
        console.error("Animation error:", err);
        item.reject(err);
      }
    }
    this.running = false;
  }

  // Decide which unit methods to call and await them.
  // AnimationController.ts
  

  private async executeCombatSequence(data: CombatResult, attacker: UnitStack, target: UnitStack, signal?: AbortSignal, isOpportunityStrike?: boolean) {
    if (!attacker || !target) return;

    try {
        await Promise.all([attacker.prepareForAction(), target.prepareForAction()]);

        const attackFinished = attacker.playAttackTowards(target);
        await attacker.waitForImpact(signal);

        const targetReaction = (async () => {
            BattleBus.emit('SKILL_EXECUTED', {
                attackerId: attacker.unitId,
                targetId: target.unitId,
                result: data // тут всі цифри: damage, isCrit, isMiss
            });
            if (data.isMiss) {
                await target.playMissReaction(attacker);
            } else {
              if (data.isCrit) {
                await target.playCritReaction(attacker);
              } else {
                await target.playHitReaction(attacker);
              }
            }
            

            if (data.isDead) {
                await target.playDeath();
                return; // Якщо мертвий, не робимо finishAction
            }
            // Ціль повертається в стійку паралельно з аткером
            await target.finishAction(signal); 
            await attacker.resetPose();
            if (!isOpportunityStrike) {
              attacker.startIdle();
            }
        })();

        // Чекаємо завершення обох анімацій
        await Promise.all([attackFinished, targetReaction]);

        // На цьому етапі обидва юніти ВЖЕ запустили свій startIdle всередині анімацій
        // Нам лишилося просто почекати паузу між ходами
        await attacker.waitForIdle(signal);
        // attacker.startIdle();

    } catch (err: any) {
        console.log("Animation sequence stopped:", err.message);
        attacker.startIdle();
        if (target && !data.isDead) target.startIdle();
    }
  }

  private async animateUnitMovement(unit: UnitStack, splinePoints: HexPoint[], signal?: AbortSignal) {
    if (!unit || !splinePoints) return;

    try {

        await unit.animateMovement(splinePoints);

    } catch (err: any) {
        console.log("Animation sequence stopped:", err.message);
    }
  }

  public cancelAll(reason = "cancelled") {
    this.queue.forEach(q => q.reject(new Error(reason)));
    this.queue = [];
    this.running = false; // Скидаємо прапорець
  }
}
